import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Box, Typography, Container, Button, CircularProgress, Card, CardContent } from '@mui/material';
import { School as SchoolIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const TeacherStudentDetailsScreen = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { learner, teacherId } = state || {};
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchResults = async () => {
    try {
      if (!learner?._id) throw new Error('Missing learner ID');
      const response = await axios.get(
        `https://kidsm.vercel.app/api/schools/learner/${learner._id}/results`
      );
      setResults(response.data.results);
    } catch (error) {
      console.error('Error fetching results:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (learner?._id) fetchResults();
  }, [learner?._id]);

  const handleEditResult = (term, examType, subject, currentMarks) => {
    navigate('/edit-result', {
      state: { learnerId: learner._id, term, examType, subject, currentMarks, onGoBack: fetchResults }
    });
  };

  const renderResults = () => {
    if (loading) {
      return (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <CircularProgress color="primary" />
        </Box>
      );
    }

    if (!results) {
      return (
        <Typography sx={{ textAlign: 'center', color: '#666', py: 5 }}>
          No results found.
        </Typography>
      );
    }

    return Object.entries(results).map(([term, examTypes]) => (
      <Box key={term} sx={{ mb: 5 }}>
        <Button
          variant="text"
          onClick={() => navigate('/analysis', { state: { term, results, examType: null } })}
          sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}
        >
          Term: {term}
        </Button>
        {Object.entries(examTypes).map(([examType, subjects]) => (
          <Box key={examType} sx={{ ml: 2, mb: 3 }}>
            <Button
              variant="text"
              onClick={() => navigate('/analysis', { state: { term, examType, results } })}
              sx={{ fontWeight: 'bold', color: '#333' }}
            >
              Exam Type: {examType}
            </Button>
            {subjects.map(({ subject, marks }, index) => (
              <Box
                key={`${subject}-${index}`}
                sx={{
                  ml: 4,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1
                }}
              >
                <Typography sx={{ fontSize: '14px', color: '#333' }}>
                  {subject}: {marks}
                </Typography>
                {/* <Button
                  variant="contained"
                  size="small"
                  sx={{ bgcolor: '#6200ee', color: '#fff' }}
                  onClick={() => handleEditResult(term, examType, subject, marks)}
                >
                  Edit
                </Button> */}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    ));
  };

  if (!learner) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="error">No learner data provided.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', pt: 8 }}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <motion.div variants={cardVariants} initial="hidden" animate="visible">
          <Typography
            variant="h4"
            sx={{ fontWeight: 'bold', color: '#333', mb: 4 }}
          >
            Student Details
          </Typography>
          <Card sx={{ p: 3, mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderRadius: '10px' }}>
            <CardContent>
              <Typography sx={{ fontSize: '16px', color: '#333', mb: 2 }}>
                Name: {learner.fullName}
              </Typography>
              <Typography sx={{ fontSize: '16px', color: '#333', mb: 2 }}>
                Admission: {learner.admissionNumber}
              </Typography>
              <Typography sx={{ fontSize: '16px', color: '#333', mb: 2 }}>
                Class: {learner.class}
              </Typography>
              {learner.stream && (
                <Typography sx={{ fontSize: '16px', color: '#333', mb: 2 }}>
                  Stream: {learner.stream}
                </Typography>
              )}
              <Typography sx={{ fontSize: '16px', color: '#333', mb: 2 }}>
                Parent: {learner.parentName}
              </Typography>
              <Typography sx={{ fontSize: '16px', color: '#333', mb: 2 }}>
                Email: {learner.parentEmail}
              </Typography>
              <Typography sx={{ fontSize: '16px', color: '#333', mb: 2 }}>
                Contact: {learner.parentContact}
              </Typography>
            </CardContent>
          </Card>

          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', color: '#333', mb: 2 }}
          >
            Results
          </Typography>
          <Card sx={{ p: 3, mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderRadius: '10px' }}>
            <CardContent>{renderResults()}</CardContent>
          </Card>

          <Box sx={{ position: 'fixed', bottom: 20, left: 20, right: 20, zIndex: 1000 }}>
            <Button
              variant="contained"
              fullWidth
              sx={{ bgcolor: '#6200ee', color: '#fff', py: 2, mb: 2, borderRadius: '10px' }}
              onClick={() => navigate('/update-results', { state: { learner } })}
            >
              Update Results
            </Button>
            <Button
              variant="contained"
              fullWidth
              sx={{ bgcolor: '#4CAF50', color: '#fff', py: 2, borderRadius: '10px' }}
              onClick={() => navigate('/update-interests', { state: { learner, teacherId } })}
            >
              Update Interests
            </Button>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default TeacherStudentDetailsScreen;